import React, { lazy, Suspense, useState, useRef, useEffect } from 'react';
import { 
  Globe, Clock, MoreVertical, Edit, Trash2, ExternalLink, Pause, Play, 
  Activity, AlertTriangle, BarChart3, Info, Server, Zap, CheckCircle, 
  XCircle, Mail, MessageCircle, Shield, Globe2, Settings, Wifi, ShieldAlert,
  ScanLine
} from 'lucide-react';
const WebsiteAnalytics = lazy(() => import('./WebsiteAnalytics'));

const formatUTCDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toISOString().replace('T', ' ').substring(0, 19);
};

function WebsiteCard({ website, onEdit, onDelete, onToggle, onScan, canActivateWebsite, activeCount, maxWebsites, darkMode = false, userPlan = 'free' }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showAIPredictionModal, setShowAIPredictionModal] = useState(false);
  const [showScanProModal, setShowScanProModal] = useState(false);
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const isProUser = userPlan === 'professional' || userPlan === 'enterprise';

  const greenGradient = 'linear-gradient(to right, rgb(152 243 0), rgba(149, 222, 26, 0.753))';
  const redGradient = 'linear-gradient(to right, rgb(255 3 0), rgb(255 0 0))';
  const orangeGradient = 'linear-gradient(to right, rgb(228 190 0), rgb(241 177 40))';
  const blueGradient = 'linear-gradient(to right, rgb(17 138 255 / 50%), rgb(36 170 253 / 75%))';
  const blacklistGradient = 'linear-gradient(to right, rgb(255 3 0), rgb(200 0 0))';

  const greenColor = '#95DE1A';
  const redColor = '#FF0000';
  const orangeColor = '#F1B128';
  const blueColor = '#118AFF';
  const purpleColor = '#8B5CF6';

  const {
    name,
    url,
    status,
    lastChecked,
    responseTime,
    uptimePercentage,
    downtimeCount,
    isActive,
    uptimeChecks = [],
    sslExpiryDate,
    sslLastChecked,
    planDays = 30,
    domainExpiryDate,
    domainAlertError,
    domainLastChecked,
    dnsLastChange,
    dnsLastChecked,
    dnsRecords,
    portMonitors = [],
    apiMonitors = [],
    pingMonitors = [],
    type = 'WEBSITE',
    notifyEmail,
    notifyTelegram,
    notifySsl,
    notifyDomain,
    notifyDns,
    lastStatus = {},
    target
  } = website;

  const isService = type === 'SERVICE';
  const isBlacklist = type === 'BLACKLIST';
  const periodLabel = planDays === 7 ? '7-day' : planDays === 30 ? '30-day' : planDays === 90 ? '90-day' : planDays === 365 ? '1-year' : `${planDays}-day`;

  const listedCount = (lastStatus && typeof lastStatus === 'object') 
    ? Object.values(lastStatus).filter(v => v === true).length 
    : 0;
  const totalRBLs = (lastStatus && typeof lastStatus === 'object') 
    ? Object.keys(lastStatus).length 
    : 0;

  const computeServiceMetrics = () => {
    const allMonitors = [...portMonitors, ...apiMonitors, ...pingMonitors];
    if (allMonitors.length === 0) return { health: null, avgResponse: null, downCount: 0, latestCheck: lastChecked };
    const upCount = allMonitors.filter(m => m.status === 'up').length;
    const health = (upCount / allMonitors.length) * 100;
    const responses = allMonitors.map(m => m.responseTime).filter(Boolean);
    const avgResponse = responses.length ? Math.round(responses.reduce((a, b) => a + b, 0) / responses.length) : null;
    const downCount = allMonitors.filter(m => m.status === 'down' || m.status === 'timeout').length;
    const checkTimes = allMonitors.map(m => m.lastChecked).filter(Boolean);
    const latestCheck = checkTimes.length ? new Date(Math.max(...checkTimes.map(d => new Date(d)))) : lastChecked;
    return { health, avgResponse, downCount, latestCheck };
  };

  const serviceMetrics = isService ? computeServiceMetrics() : null;
  const displayUptime = isService ? serviceMetrics?.health : uptimePercentage;
  const displayResponseTime = isService ? serviceMetrics?.avgResponse : responseTime;
  const displayDowntimeCount = isService ? serviceMetrics?.downCount : downtimeCount;
  const displayLastChecked = isService ? serviceMetrics?.latestCheck : lastChecked;

  const getSSLStatus = () => {
    if (!url?.startsWith('https://') || isService || isBlacklist) return null;
    if (!sslExpiryDate) return { text: 'Error / No Certificate', color: redColor, icon: AlertTriangle };
    const expiry = new Date(sslExpiryDate);
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { text: 'Expired', color: redColor, icon: AlertTriangle };
    if (daysLeft <= 7) return { text: `Expires in ${daysLeft} days`, color: orangeColor, icon: AlertTriangle };
    if (daysLeft <= 14) return { text: `Expires in ${daysLeft} days`, color: orangeColor, icon: AlertTriangle };
    if (daysLeft <= 30) return { text: `Expires in ${daysLeft} days`, color: redColor, icon: AlertTriangle };
    return { text: `Expires in ${daysLeft} days`, color: greenColor, icon: null };
  };
  const sslStatus = getSSLStatus();

  const getDomainStatus = () => {
    if (isService || isBlacklist) return null;
    if (!domainExpiryDate && !domainAlertError && !domainLastChecked) return { text: 'Not checked yet', color: '#9CA3AF', icon: Info };
    if (domainAlertError) return { text: 'Unavailable', note: 'Data may not be shared due to local regulations', color: redColor, icon: AlertTriangle };
    if (!domainExpiryDate) return { text: 'No expiry date', color: '#9CA3AF', icon: Info };
    const expiry = new Date(domainExpiryDate);
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    let text, color;
    if (daysLeft < 0) { text = 'Expired'; color = redColor; }
    else if (daysLeft <= 7) { text = `Expires in ${daysLeft} days`; color = orangeColor; }
    else if (daysLeft <= 14) { text = `Expires in ${daysLeft} days`; color = orangeColor; }
    else if (daysLeft <= 30) { text = `Expires in ${daysLeft} days`; color = redColor; }
    else { text = `Expires in ${daysLeft} days`; color = greenColor; }
    return { text, color, daysLeft, expiry, icon: null };
  };
  const domainStatus = getDomainStatus();

  let parsedDnsRecords = null;
  try {
    parsedDnsRecords = dnsRecords ? JSON.parse(dnsRecords) : null;
  } catch (e) { console.error('DNS records parse error:', e); }

  const recentChecks = uptimeChecks.slice(0, 30).reverse();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'up': return <Activity className="h-4 w-4" />;
      case 'down': return <AlertTriangle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };
  const getServiceStatusIcon = (status) => {
    switch (status) {
      case 'up': return <CheckCircle className="h-4 w-4" style={{ color: greenColor }} />;
      case 'down': return <XCircle className="h-4 w-4" style={{ color: redColor }} />;
      default: return <Clock className="h-4 w-4" style={{ color: orangeColor }} />;
    }
  };
  const getBlacklistStatusIcon = (status) => {
    switch (status) {
      case 'clean': return <CheckCircle className="h-4 w-4" style={{ color: greenColor }} />;
      case 'listed': return <AlertTriangle className="h-4 w-4" style={{ color: redColor }} />;
      default: return <Clock className="h-4 w-4" style={{ color: orangeColor }} />;
    }
  };

  const formatLastChecked = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const checked = new Date(date);
    const diff = now - checked;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  const formatUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname + (urlObj.pathname !== '/' ? urlObj.pathname : '');
    } catch { return url; }
  };

  const canResume = () => {
    if (isActive) return true;
    if (canActivateWebsite) return canActivateWebsite(website);
    return activeCount < maxWebsites;
  };
  const isResumeDisabled = !isActive && !canResume();

  const getStatusDisplay = () => {
    const statusUpper = status ? status.toUpperCase() : 'UNKNOWN';
    switch (type) {
      case 'WEBSITE': return `Website ${statusUpper}`;
      case 'PORT': return `Port ${statusUpper}`;
      case 'API': return `API ${statusUpper}`;
      case 'PING': return `Ping ${statusUpper}`;
      case 'SERVICE': return `Service ${statusUpper}`;
      case 'BLACKLIST': return `Blacklist ${statusUpper}`;
      default: return `${type} ${statusUpper}`;
    }
  };

  const getIconConfig = () => {
    if (darkMode) {
      return { 
        bgClass: 'bg-gray-700/50',
        iconColor: (() => {
          switch (type) {
            case 'WEBSITE': return '#95DE1A';
            case 'PORT': return '#0b7dc3';
            case 'API': return '#F97316';
            case 'PING': return '#0b7dc3';
            case 'SERVICE': return '#0b7dc3';
            case 'BLACKLIST': return '#FF0000';
            default: return '#9CA3AF';
          }
        })()
      };
    }
    
    switch (type) {
      case 'WEBSITE':
        return { gradient: 'from-emerald-100 to-emerald-50', iconColor: '#95DE1A' };
      case 'PORT':
        return { gradient: 'from-blue-100 to-blue-50', iconColor: '#0b7dc3' };
      case 'API':
        return { gradient: 'from-amber-100 to-amber-50', iconColor: '#F97316' };
      case 'PING':
        return { gradient: 'from-cyan-100 to-cyan-50', iconColor: '#0b7dc3' };
      case 'SERVICE':
        return { gradient: 'from-indigo-100 to-indigo-50', iconColor: '#0b7dc3' };
      case 'BLACKLIST':
        return { gradient: 'from-red-100 to-red-50', iconColor: '#FF0000' };
      default:
        return { gradient: 'from-gray-100 to-gray-50', iconColor: '#6B7280' };
    }
  };

  const iconConfig = getIconConfig();

  const getTypeIcon = () => {
    switch (type) {
      case 'WEBSITE': return <Globe className="h-6 w-6" stroke={iconConfig.iconColor} strokeWidth={2} fill="none" />;
      case 'PORT': return <Server className="h-6 w-6" stroke={iconConfig.iconColor} strokeWidth={2} fill="none" />;
      case 'API': return <Zap className="h-6 w-6" stroke={iconConfig.iconColor} strokeWidth={2} fill="none" />;
      case 'PING': return <Wifi className="h-6 w-6" stroke={iconConfig.iconColor} strokeWidth={2} fill="none" />;
      case 'SERVICE': return <Activity className="h-6 w-6" stroke={iconConfig.iconColor} strokeWidth={2} fill="none" />;
      case 'BLACKLIST': return <ShieldAlert className="h-6 w-6" stroke={iconConfig.iconColor} strokeWidth={2} fill="none" />;
      default: return <Globe className="h-6 w-6" stroke={iconConfig.iconColor} strokeWidth={2} fill="none" />;
    }
  };

  const handleAIPrediction = () => {
    if (!isProUser) {
      setShowAIPredictionModal(true);
      return;
    }
    setShowAnalytics(true);
  };

  const handleScanClick = () => {
    if (!isProUser) {
      setShowScanProModal(true);
      return;
    }
    if (onScan) {
      onScan(website);
    }
  };

  const handleMouseEnter = (id) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setActiveTooltipId(id);
  };
  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setActiveTooltipId(null), 800);
  };
  const handleTooltipMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };
  const handleTooltipMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setActiveTooltipId(null), 800);
  };
  useEffect(() => {
    return () => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); };
  }, []);

  return (
    <>
      <div className={`rounded-3xl shadow-lg transition-all duration-500 hover:shadow-xl hover:-translate-y-1 group ${!isActive ? 'opacity-60' : ''} ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="px-6 py-4 border-b border-gray-100 rounded-t-3xl" style={{
          background: isService ? blueGradient
            : isBlacklist ? blacklistGradient
            : status === 'up' ? greenGradient
            : status === 'down' ? redGradient
            : orangeGradient
        }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 px-4 py-2 rounded-2xl border bg-white/90 backdrop-blur-sm" style={status === 'up' ? { color: greenColor, borderColor: greenColor } : status === 'down' ? { color: redColor, borderColor: redColor } : isBlacklist ? { color: '#FF0000', borderColor: '#FF0000' } : { color: orangeColor, borderColor: orangeColor }}>
              {isService ? getServiceStatusIcon(status) : isBlacklist ? getBlacklistStatusIcon(status) : getStatusIcon(status)}
              <span className="font-semibold text-sm">
                {!isActive && <Pause className="h-3 w-3 mr-1 inline" />}
                {getStatusDisplay()}
                {!isActive && ' (Paused)'}
              </span>
            </div>

            <div className="relative">
              <button onClick={() => setShowMenu(!showMenu)} className="p-2 rounded-xl hover:bg-black/20 transition-colors bg-black/20">
                <MoreVertical className="h-5 w-5 text-white" strokeWidth={2} fill="none" />
              </button>
              {showMenu && (
                <div className={`absolute right-0 top-12 w-40 rounded-2xl shadow-xl border py-2 z-20 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <button onClick={() => { onEdit(); setShowMenu(false); }} className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-blue-50'}`}>
                    <Edit className="h-4 w-4" strokeWidth={2} fill="none" /><span>Edit</span>
                  </button>
                  {!isBlacklist && (
                    <button onClick={() => { handleAIPrediction(); setShowMenu(false); }} className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-blue-50'}`}>
                      <BarChart3 className="h-4 w-4" strokeWidth={2} fill="none" /><span>AI Analytics</span>
                    </button>
                  )}
                  {!isBlacklist && onScan && (
                    <button onClick={() => { handleScanClick(); setShowMenu(false); }} className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${darkMode ? 'text-purple-400 hover:bg-gray-700' : 'text-purple-600 hover:bg-purple-50'}`}>
                      <ScanLine className="h-4 w-4" strokeWidth={2} fill="none" /><span>Scan</span>
                    </button>
                  )}
                  <button onClick={() => { onToggle(website.id, isActive); setShowMenu(false); }} disabled={isResumeDisabled} className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${isActive ? (darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50') : (darkMode ? 'text-green-400 hover:bg-gray-700' : 'text-green-600 hover:bg-green-50')} ${isResumeDisabled ? 'opacity-50 cursor-not-allowed' : ''}`} title={isResumeDisabled ? `Maximum ${maxWebsites} active websites allowed. Deactivate another website first.` : ''}>
                    {isActive ? <Pause className="h-4 w-4" strokeWidth={2} fill="none" /> : <Play className="h-4 w-4" strokeWidth={2} fill="none" />}<span>{isActive ? 'Pause' : 'Resume'}</span>
                  </button>
                  <button onClick={() => { onDelete(); setShowMenu(false); }} className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors ${darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'}`}>
                    <Trash2 className="h-4 w-4" strokeWidth={2} fill="none" /><span>Delete</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-start space-x-4 mb-6">
            <div className="relative flex-shrink-0">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center
                ${darkMode 
                  ? iconConfig.bgClass 
                  : `bg-gradient-to-br ${iconConfig.gradient}`
                }`}
              >
                {getTypeIcon()}
              </div>
              {!isService && !isBlacklist && (
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${status === 'down' ? 'bg-red-500' : status === 'unknown' ? 'bg-yellow-500' : ''} ${status === 'up' ? 'animate-pulse' : ''}`} style={status === 'up' ? { backgroundColor: greenColor } : {}}></div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={`text-xl font-bold truncate mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} title={name}>{name}</h3>
              {isBlacklist && target && (
                <p className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} title={target}>Target: {target}</p>
              )}
              {!isBlacklist && url && type !== 'SERVICE' ? (
                <a href={type === 'WEBSITE' ? url : `http://${url}`} target="_blank" rel="noopener noreferrer" className={`text-sm flex items-center space-x-2 group/link ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`} title={url}>
                  <span className="truncate max-w-[200px]">{formatUrl(url)}</span>
                  <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" strokeWidth={2} fill="none" />
                </a>
              ) : (
                !isBlacklist && <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{url ? formatUrl(url) : 'No URL'}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            {isBlacklist ? (
              <>
                <div className="rounded-2xl p-4 text-white" style={{ background: listedCount > 0 ? redGradient : greenGradient }}>
                  <div className="text-2xl font-bold mb-1">{listedCount}</div>
                  <div className="text-xs font-medium text-white/80">Listed RBLs</div>
                </div>
                <div className={`rounded-2xl p-4 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-blue-50 to-cyan-50'}`}>
                  <div className="text-2xl font-bold mb-1" style={{ color: blueColor }}>{totalRBLs}</div>
                  <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-blue-700'}`}>Total RBLs</div>
                </div>
                <div className={`rounded-2xl p-4 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-purple-50 to-indigo-50'}`}>
                  <div className="text-2xl font-bold mb-1" style={{ color: purpleColor }}>{formatLastChecked(lastChecked)}</div>
                  <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-purple-700'}`}>Last checked</div>
                </div>
                <div className="rounded-2xl p-4 text-white" style={{ background: status === 'clean' ? greenGradient : status === 'listed' ? redGradient : orangeGradient }}>
                  <div className="text-2xl font-bold mb-1">{status ? status.toUpperCase() : 'UNKNOWN'}</div>
                  <div className="text-xs font-medium text-white/80">Overall Status</div>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-2xl p-4 text-white" style={{
                  background: displayUptime !== null && displayUptime >= 99 ? `linear-gradient(to right, ${greenColor}, #7EC50A)`
                    : displayUptime !== null && displayUptime >= 95 ? `linear-gradient(to right, ${orangeColor}, #D00C09)`
                    : displayUptime !== null ? `linear-gradient(to right, ${redColor}, #D00C09)`
                    : `linear-gradient(to right, #9CA3AF, #6B7280)`
                }}>
                  <div className="text-2xl font-bold mb-1">{displayUptime !== null ? `${displayUptime.toFixed(1)}%` : 'N/A'}</div>
                  <div className="text-xs font-medium text-white/80">{isService ? 'Monitors health' : `${periodLabel} uptime`}</div>
                </div>
                <div className={`rounded-2xl p-4 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-blue-50 to-cyan-50'}`}>
                  <div className="text-2xl font-bold mb-1" style={{ color: blueColor }}>{displayResponseTime ? `${displayResponseTime}ms` : 'N/A'}</div>
                  <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-blue-700'}`}>Response time</div>
                </div>
                <div className={`rounded-2xl p-4 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-orange-50 to-red-50'}`}>
                  <div className="text-2xl font-bold mb-1" style={{ color: orangeColor }}>{displayDowntimeCount || 0}</div>
                  <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-orange-700'}`}>{isService ? 'Monitors down' : `Incidents (${periodLabel})`}</div>
                </div>
                <div className={`rounded-2xl p-4 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-purple-50 to-indigo-50'}`}>
                  <div className="text-2xl font-bold mb-1" style={{ color: purpleColor }}>{formatLastChecked(displayLastChecked)}</div>
                  <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-purple-700'}`}>Last checked</div>
                </div>
              </>
            )}
          </div>

          {isBlacklist && totalRBLs > 0 && lastStatus && (
            <div className="mt-4">
              <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>RBL Status</h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {Object.entries(lastStatus).map(([rbl, isListed]) => (
                  <div key={rbl} className="flex items-center justify-between text-sm">
                    <span className={`truncate max-w-[70%] ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} title={rbl}>{rbl}</span>
                    <span className={`font-semibold ${isListed ? 'text-red-600' : 'text-green-600'}`}>
                      {isListed ? 'LISTED' : 'Clean'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isBlacklist && recentChecks.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Recent checks</span>
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{recentChecks.length} checks</span>
              </div>
              <div className="flex items-end space-x-1 h-8">
                {recentChecks.map((check, index) => (
                  <div key={check.id || index} className="tick-indicator group/tick relative flex-1 cursor-pointer" style={{
                    backgroundColor: check.status === 'up' ? greenColor : check.status === 'down' ? '#EF4444' : '#D1D5DB',
                    height: check.responseTime ? `${Math.min(Math.max(check.responseTime / 10, 4), 32)}px` : '4px'
                  }}>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover/tick:block bg-gray-900 text-white text-xs rounded py-1 px-2 z-50 whitespace-nowrap">
                      <div>{check.status.toUpperCase()} - {check.responseTime ? `${check.responseTime}ms` : 'No response'}</div>
                      <div>{formatUTCDateTime(check.timestamp).split(' ')[0]}</div>
                      <div>{formatUTCDateTime(check.timestamp).split(' ')[1]} UTC</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1"><span>Oldest</span><span>Latest</span></div>
            </div>
          )}

          {/* Notification Preferences */}
          <div className="mt-6 border-t pt-4 dark:border-gray-700">
            <h4 className={`text-sm font-semibold mb-3 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <Settings className="h-4 w-4 mr-1" strokeWidth={2} fill="none" /> 
              Notification Preferences
            </h4>
            <div className="flex flex-wrap gap-2">
              {notifyEmail !== undefined && notifyEmail && (
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                  darkMode 
                    ? 'bg-green-900/40 text-green-400 border border-green-500/50' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  <Mail className="h-3 w-3" strokeWidth={2} fill="none" />
                  <span>Email</span>
                </div>
              )}
              {notifyTelegram !== undefined && notifyTelegram && (
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                  darkMode 
                    ? 'bg-sky-900/40 text-sky-400 border border-sky-500/50' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  <MessageCircle className="h-3 w-3" strokeWidth={2} fill="none" />
                  <span>Telegram</span>
                </div>
              )}
              {!isBlacklist && notifySsl !== undefined && notifySsl && (
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                  darkMode 
                    ? 'bg-amber-900/40 text-amber-400 border border-amber-500/50' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  <Shield className="h-3 w-3" strokeWidth={2} fill="none" />
                  <span>SSL</span>
                </div>
              )}
              {!isBlacklist && notifyDomain !== undefined && notifyDomain && (
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                  darkMode 
                    ? 'bg-purple-900/40 text-purple-400 border border-purple-500/50' 
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  <Globe2 className="h-3 w-3" strokeWidth={2} fill="none" />
                  <span>Domain</span>
                </div>
              )}
              {!isBlacklist && notifyDns !== undefined && notifyDns && (
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                  darkMode 
                    ? 'bg-indigo-900/40 text-indigo-400 border border-indigo-500/50' 
                    : 'bg-indigo-100 text-indigo-700'
                }`}>
                  <Activity className="h-3 w-3" strokeWidth={2} fill="none" />
                  <span>DNS</span>
                </div>
              )}
              {(!notifyEmail && !notifyTelegram && 
                (isBlacklist || (!notifySsl && !notifyDomain && !notifyDns))) && (
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>None</span>
              )}
            </div>
          </div>

          {!isService && !isBlacklist && sslStatus && (
            <div className="mt-4 group/ssl relative">
              <div className={`rounded-2xl p-4 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-indigo-50 to-blue-50'}`}>
                <div className="flex items-center justify-between"><span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>SSL Certificate</span><span className="flex items-center space-x-1 font-bold" style={{ color: sslStatus.color }}>{sslStatus.icon && <sslStatus.icon className="h-4 w-4" strokeWidth={2} fill="none" />}<span>{sslStatus.text}</span></span></div>
                {sslExpiryDate && <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Expires: {new Date(sslExpiryDate).toISOString().substring(0, 10)} (UTC)</div>}
              </div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover/ssl:block bg-gray-900 text-white text-xs rounded py-1 px-2 z-50 whitespace-nowrap">
                <div>Last checked: {sslLastChecked ? formatLastChecked(sslLastChecked) : 'Never'}</div>
                {sslExpiryDate && <div>Expires: {new Date(sslExpiryDate).toISOString().substring(0, 10)} UTC</div>}
              </div>
            </div>
          )}

          {!isService && !isBlacklist && domainStatus && (
            <div className="mt-4 group/domain relative">
              <div className={`rounded-2xl p-4 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-indigo-50 to-blue-50'}`}>
                <div className="flex items-center justify-between"><span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Domain Expiry</span><span className="flex items-center space-x-1 font-bold" style={{ color: domainStatus.color }}>{domainStatus.icon && <domainStatus.icon className="h-4 w-4" strokeWidth={2} fill="none" />}<span>{domainStatus.text}</span></span></div>
                {domainStatus.note && <div className={`text-xs mt-1 italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{domainStatus.note}</div>}
                {domainExpiryDate && <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Expires: {new Date(domainExpiryDate).toISOString().substring(0, 10)} (UTC)</div>}
                {domainLastChecked && !domainExpiryDate && !domainAlertError && <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last checked: {formatLastChecked(domainLastChecked)}</div>}
              </div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover/domain:block bg-gray-900 text-white text-xs rounded py-1 px-2 z-50 whitespace-nowrap">
                <div>Last checked: {domainLastChecked ? formatLastChecked(domainLastChecked) : 'Never'}</div>
                {domainExpiryDate && <div>Expires: {new Date(domainExpiryDate).toISOString().substring(0, 10)} UTC</div>}
                {domainAlertError && <div>Error: {domainStatus.text}</div>}
              </div>
            </div>
          )}

          {!isBlacklist && (
            <div className="mt-4 group/dns relative">
              <div className={`rounded-2xl p-4 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-indigo-50 to-blue-50'}`}>
                <div className="flex items-center justify-between"><span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>DNS Records</span>{dnsLastChange ? <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{new Date(dnsLastChange).toISOString().substring(0, 10)} (UTC)</span> : <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Not checked</span>}</div>
                {parsedDnsRecords ? <div className={`text-xs mt-1 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} title={parsedDnsRecords.join(', ')}>Records: {parsedDnsRecords.join(', ')}</div> : <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No A records found</div>}
              </div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover/dns:block bg-gray-900 text-white text-xs rounded py-1 px-2 z-50 whitespace-nowrap">
                <div>Last checked: {dnsLastChecked ? formatLastChecked(dnsLastChecked) : 'Never'}</div>
                {dnsLastChange && <div>Last change: {new Date(dnsLastChange).toISOString().substring(0, 10)} UTC</div>}
                {parsedDnsRecords && <div>Records: {parsedDnsRecords.length} A record(s)</div>}
              </div>
            </div>
          )}

          {!isService && !isBlacklist && uptimePercentage !== undefined && (
            <div className={`rounded-2xl p-4 mt-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-2"><span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Overall Health</span><span className="text-sm font-bold" style={{ color: greenColor }}>{uptimePercentage.toFixed(1)}%</span></div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 overflow-hidden">
                <div className="h-3 rounded-full transition-all duration-1000" style={{ background: uptimePercentage >= 99 ? `linear-gradient(to right, ${greenColor}, #7EC50A)` : uptimePercentage >= 95 ? `linear-gradient(to right, ${orangeColor}, #D00C09)` : `linear-gradient(to right, ${redColor}, #D00C09)`, width: `${Math.max(uptimePercentage, 2)}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {showAnalytics && (
        <Suspense fallback={
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 text-white" role="status">
            Loading analytics…
          </div>
        }>
          <WebsiteAnalytics website={website} onClose={() => setShowAnalytics(false)} />
        </Suspense>
      )}

      {/* AI Prediction Pro Modal */}
      {showAIPredictionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`rounded-3xl shadow-2xl w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${blueColor}20, ${blueColor}40)` }}>
                <BarChart3 className="h-10 w-10" style={{ color: blueColor }} />
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                AI Outage Prediction
              </h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Upgrade to Professional or Enterprise to unlock AI-powered outage predictions and advanced analytics.
                Get proactive insights about potential downtime before it happens.
              </p>
              <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  With AI Outage Prediction you get:
                </p>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>• ML-powered outage forecasting</li>
                  <li>• 7-day predictive analytics</li>
                  <li>• Anomaly detection</li>
                  <li>• Smart alerts before failures</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAIPredictionModal(false)}
                  className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowAIPredictionModal(false);
                    window.dispatchEvent(new CustomEvent('openPricingModal'));
                  }}
                  className="flex-1 px-4 py-2 rounded-xl font-medium text-white transition-all hover:shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${blueColor}, #1e40af)` }}
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan Pro Modal */}
      {showScanProModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`rounded-3xl shadow-2xl w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${purpleColor}20, ${purpleColor}40)` }}>
                <ScanLine className="h-10 w-10" style={{ color: purpleColor }} />
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Professional Feature
              </h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Upgrade to Professional or Enterprise to unlock advanced security scanning powered by Nuclei.
                Scan your domains and IP addresses for CVEs, misconfigurations, vulnerabilities, and exposures.
              </p>
              <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  With Professional you get:
                </p>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>• Full Nuclei template access (CVEs, misconfigurations, exposures)</li>
                  <li>• Advanced vulnerability detection</li>
                  <li>• Detailed security reports</li>
                  <li>• Continuous monitoring</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowScanProModal(false)}
                  className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowScanProModal(false);
                    window.dispatchEvent(new CustomEvent('openPricingModal'));
                  }}
                  className="flex-1 px-4 py-2 rounded-xl font-medium text-white transition-all hover:shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${purpleColor}, #6D28D9)` }}
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default WebsiteCard;
